// =============================================================================
// profileFields.ts — flatten a Profile into fillable values (v0.2.39)
// =============================================================================
//
// The autofill feature is "click-to-fill": the extension shows the applicant's
// profile values as a flat, grouped list; the user clicks a form field then a
// value to fill it. This module turns the structured Profile object into that
// flat list.
//
// Date of birth is deliberately EXCLUDED (per the v0.2.39 design decision).
// Structured/list data (activities, schools, etc.) is flattened to its
// individual text pieces — an array can't fill a single form field.
// =============================================================================

import type { Profile } from '@scholarship-app/types';

/** One fillable value the user can click to insert into a form field. */
export interface FillableField {
  /** Stable key, unique within the field list. */
  key: string;
  /** Human label shown in the panel, e.g. "Legal first name". */
  label: string;
  /** The value to fill. Always a string (numbers/booleans stringified). */
  value: string;
  /** Group heading for the panel. */
  group: FillGroup;
}

export type FillGroup =
  | 'Identity'
  | 'Contact'
  | 'Education'
  | 'Test scores'
  | 'Academics'
  | 'Activities & honors'
  | 'Affiliations'
  | 'Other';

function str(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

/**
 * Flatten a Profile into the list of fillable fields. Only fields with a
 * non-empty value are included — there's nothing to click-fill from a blank.
 * Date of birth is never included.
 */
export function profileToFillableFields(p: Profile): FillableField[] {
  const out: FillableField[] = [];
  const add = (key: string, label: string, value: unknown, group: FillGroup) => {
    const s = str(value).trim();
    if (s) out.push({ key, label, value: s, group });
  };

  // --- Identity (NOTE: date_of_birth deliberately omitted) ---
  add('legal_first_name', 'Legal first name', p.legal_first_name, 'Identity');
  add('legal_middle_name', 'Legal middle name', p.legal_middle_name, 'Identity');
  add('legal_last_name', 'Legal last name', p.legal_last_name, 'Identity');
  add('preferred_name', 'Preferred name', p.preferred_name, 'Identity');
  const fullName = [p.legal_first_name, p.legal_middle_name, p.legal_last_name]
    .filter(Boolean).join(' ');
  add('full_name', 'Full legal name', fullName, 'Identity');
  add('gender', 'Gender', p.gender, 'Identity');
  add('ethnicity', 'Ethnicity', (p.ethnicity ?? []).join(', '), 'Identity');
  add('citizenship', 'Citizenship', p.citizenship, 'Identity');

  // --- Contact ---
  if (p.address) {
    add('address_street', 'Street address', p.address.street, 'Contact');
    add('address_city', 'City', p.address.city, 'Contact');
    add('address_state', 'State', p.address.state, 'Contact');
    add('address_zip', 'ZIP code', p.address.zip, 'Contact');
    const oneLine = [
      p.address.street,
      [p.address.city, p.address.state].filter(Boolean).join(', '),
      p.address.zip,
    ].filter(Boolean).join(', ');
    add('address_full', 'Full address', oneLine, 'Contact');
  }
  add('phone', 'Phone', p.phone, 'Contact');
  add('email', 'Email', p.email, 'Contact');

  // --- Education ---
  add('schooling_type', 'Schooling type', p.schooling_type, 'Education');
  add('homeschool_co_op', 'Homeschool co-op', p.homeschool_co_op, 'Education');
  add('homeschool_legal_name', 'Homeschool legal name', p.homeschool_legal_name, 'Education');
  add('homeschool_admin_name', 'Homeschool administrator', p.homeschool_admin_name, 'Education');
  add('expected_graduation', 'Expected graduation', p.expected_graduation, 'Education');
  add('current_grade', 'Current grade', p.current_grade, 'Education');
  add('gpa_weighted', 'GPA (weighted)', p.gpa_weighted, 'Education');
  add('gpa_unweighted', 'GPA (unweighted)', p.gpa_unweighted, 'Education');
  add('class_rank', 'Class rank', p.class_rank, 'Education');
  add('class_size', 'Class size', p.class_size, 'Education');

  // --- Test scores ---
  if (p.test_scores) {
    const ts = p.test_scores;
    // Highest ACT/SAT composite as a convenient single value.
    const bestAct = (ts.act ?? []).reduce<number | null>(
      (m, a) => (m == null || a.score > m ? a.score : m), null);
    const bestSat = (ts.sat ?? []).reduce<number | null>(
      (m, s) => (m == null || s.score > m ? s.score : m), null);
    add('act_best', 'ACT (highest)', bestAct, 'Test scores');
    add('sat_best', 'SAT (highest)', bestSat, 'Test scores');
    (ts.act ?? []).forEach((a, i) => {
      add(`act_${i}`, `ACT score${a.date ? ` (${a.date})` : ''}`, a.score, 'Test scores');
    });
    (ts.sat ?? []).forEach((s, i) => {
      add(`sat_${i}`, `SAT score${s.date ? ` (${s.date})` : ''}`, s.score, 'Test scores');
    });
  }

  // --- Academics ---
  add('intended_majors', 'Intended major(s)', (p.intended_majors ?? []).join(', '), 'Academics');
  add('career_path', 'Career path', p.career_path, 'Academics');
  add('academic_strengths', 'Academic strengths',
    (p.academic_strengths ?? []).join(', '), 'Academics');
  add('languages_spoken', 'Languages spoken',
    (p.languages_spoken ?? []).join(', '), 'Academics');
  if (p.dual_enrollment?.institution_name) {
    add('dual_enrollment_institution', 'Dual-enrollment institution',
      p.dual_enrollment.institution_name, 'Academics');
  }

  // --- Activities & honors (flattened: each entry's name becomes fillable) ---
  (p.extracurriculars ?? []).forEach((e, i) => {
    add(`extracurricular_${i}`, `Activity: ${e.name || `#${i + 1}`}`,
      e.name, 'Activities & honors');
  });
  (p.work_experience ?? []).forEach((e, i) => {
    add(`work_${i}`, `Work: ${e.name || `#${i + 1}`}`,
      e.name, 'Activities & honors');
  });
  (p.honors_awards ?? []).forEach((h, i) => {
    add(`honor_${i}`, `Honor: ${h.name || `#${i + 1}`}`, h.name, 'Activities & honors');
  });
  add('community_service_hours_total', 'Community service hours (total)',
    p.community_service_hours_total, 'Activities & honors');

  // --- Affiliations ---
  add('religious_affiliation', 'Religious affiliation', p.religious_affiliation, 'Affiliations');
  add('lutheran_synod', 'Lutheran synod', p.lutheran_synod, 'Affiliations');
  add('church_name', 'Church name', p.church_name, 'Affiliations');
  add('church_city', 'Church city', p.church_city, 'Affiliations');

  // --- Other ---
  add('first_generation', 'First-generation college student',
    p.first_generation_college_student, 'Other');
  add('household_income_bracket', 'Household income bracket',
    p.household_income_bracket, 'Other');
  add('special_circumstances', 'Special circumstances', p.special_circumstances, 'Other');

  return out;
}

/** The fields grouped by their FillGroup, in display order. */
export function groupFillableFields(
  fields: FillableField[]
): { group: FillGroup; fields: FillableField[] }[] {
  const order: FillGroup[] = [
    'Identity', 'Contact', 'Education', 'Test scores',
    'Academics', 'Activities & honors', 'Affiliations', 'Other',
  ];
  return order
    .map((group) => ({ group, fields: fields.filter((f) => f.group === group) }))
    .filter((g) => g.fields.length > 0);
}
